import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { IAcceptPolicyDto, IPendingPolicyDto } from '@app/interfaces/policy.interface';
import { IResponseItem } from '@app/interfaces/response-item.interface';
import { IUserDto } from '@app/interfaces/user.interface';
import { UserModel } from '@app/models/user.model';
import { POLICY_SCOPE_ALL } from '@app/utils/constants';
import { tools } from '@app/utils/tools';
import { translations } from '@app/utils/translations';
import { environment } from '@env/environment';
import { catchError, map, Observable, of } from 'rxjs';

const ENDPOINT = environment.api + '/users/';

@Injectable({
    providedIn: 'root',
})
export class UsersHttpService {
    private http: HttpClient = inject(HttpClient);

    get() {
        return this.http
            .get<IUserDto[]>(ENDPOINT)
            .pipe(map(users => users.map(p => new UserModel(p))));
    }

    getById(id?: string) {
        return this.http.get<IUserDto>(ENDPOINT + id).pipe(map(user => new UserModel(user)));
    }

    create(user: UserModel): Observable<IResponseItem<UserModel>> {
        return this.http.post<IUserDto>(ENDPOINT, user.dataTransferObject()).pipe(
            map(user => ({
                value: new UserModel(user),
            })),
            catchError(error => {
                return of({
                    error: {
                        error: true,
                        message: tools.getErrorTranslation(error, translations.users.error_add),
                    },
                });
            }),
        );
    }

    update(user: UserModel): Observable<IResponseItem<UserModel>> {
        return this.http.put<IUserDto>(ENDPOINT + user.id, user.dataTransferObject()).pipe(
            map(user => ({
                value: new UserModel(user),
            })),
            catchError(error => {
                return of({
                    error: {
                        error: true,
                        message: tools.getErrorTranslation(error, translations.users.error_update),
                    },
                });
            }),
        );
    }

    delete(id: string): Observable<IResponseItem<UserModel>> {
        return this.http.delete(ENDPOINT + id).pipe(
            map(() => ({
                success: true,
            })),
            catchError(error =>
                of({
                    error: {
                        error: true,
                        message: tools.getErrorTranslation(error, translations.users.error_delete),
                    },
                }),
            ),
        );
    }

    elev8Sync(id: string) {
        return this.http.post(ENDPOINT + id + '/sync-eleveight', null);
    }

    sendPassword(id: string) {
        return this.http.post(ENDPOINT + id + '/send-password', null);
    }

    acceptPolicies(policies: IAcceptPolicyDto[]) {
        return this.http.put<void>(`${ENDPOINT}me/accepted-policies`, policies);
    }

    getPendingPolicies(): Observable<IPendingPolicyDto[]> {
        return this.http.get<IPendingPolicyDto[]>(`${ENDPOINT}me/pending-policies`, {
            params: { scope: POLICY_SCOPE_ALL },
        });
    }

    getUserData(): Observable<IResponseItem<UserModel>> {
        return this.http.get<IUserDto>(ENDPOINT + 'me').pipe(
            map(user => ({
                success: true,
                value: new UserModel(user),
            })),
            catchError(error => {
                return of({
                    error: {
                        error: true,
                        message: tools.getErrorTranslation(error, translations.users.error_update),
                    },
                });
            }),
        );
    }
}
